import React, { useState } from 'react';
import { Calculator, History, Book, StepBack as Steps, X } from 'lucide-react';
import { UnitCategory } from '../types/units';
import { convertValue } from '../utils/conversions';
import { unitsByCategory } from '../utils/units';
import { commonFormulas } from '../utils/formulas';

const descriptions = {
  title: "Smart Calculator",
  description: "Perform complex calculations with mixed units. Use mathematical operations, combine different units, and see step-by-step solutions.",
  features: [
    "Support for +, -, *, / operations",
    "Automatic unit conversion",
    "Step-by-step calculation breakdown",
    "Save and reuse common calculations"
  ]
};

interface SmartCalculatorProps {
  category: UnitCategory;
}

interface CalculationHistoryItem {
  expression: string;
  result: string;
  timestamp: Date;
}

export const SmartCalculator: React.FC<SmartCalculatorProps> = ({ category }) => {
  const [expression, setExpression] = useState<string>('');
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<CalculationHistoryItem[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showFormulas, setShowFormulas] = useState(false);
  const [steps, setSteps] = useState<string[]>([]);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [selectedFormula, setSelectedFormula] = useState<Formula | null>(null);

  const validateUnits = (expr: string) => {
    const tokens = expr.match(/[a-zA-Z_]+/g) || [];
    const validUnits = new Set(unitsByCategory[category].map(u => u.value));
    const invalidUnits = tokens.filter(token => !validUnits.has(token));
    
    if (invalidUnits.length > 0) {
      const suggestions = unitsByCategory[category]
        .map(u => u.value)
        .filter(unit => 
          invalidUnits.some(invalid => 
            unit.startsWith(invalid) || unit.endsWith(invalid)
          )
        );
      setSuggestions(suggestions);
      return false;
    }
    
    setSuggestions([]);
    return true;
  };

  const evaluateExpression = (expr: string) => {
    try {
      if (!validateUnits(expr)) {
        setError('Invalid units detected. Check suggestions below.');
        return;
      }

      const newSteps: string[] = ['Original expression: ' + expr];
      
      // Basic tokenization of the expression
      const tokens = expr.match(/(-?\d*\.?\d+)\s*([a-zA-Z_]+)|\+|\-|\*|\/|\(|\)|\s+/g) || [];
      
      // Convert all values to base units for the category
      let processedExpr = expr;
      let unitUsed = '';
      
      tokens.forEach(token => {
        const valueUnitMatch = token.match(/(-?\d*\.?\d+)\s*([a-zA-Z_]+)/);
        if (valueUnitMatch) {
          const [_, value, unit] = valueUnitMatch;
          const baseUnit = unitsByCategory[category][0].value;
          const converted = convertValue(parseFloat(value), unit, baseUnit, category);
          processedExpr = processedExpr.replace(token, converted.toString());
          unitUsed = unit;
          newSteps.push(`Converting ${value}${unit} to ${converted.toFixed(4)}${baseUnit}`);
        }
      });

      newSteps.push('Normalized expression: ' + processedExpr);

      // Safely evaluate the processed expression
      const evalResult = Function('"use strict";return (' + processedExpr + ')')();
      
      // Convert back to the last used unit
      const finalResult = unitUsed ? 
        convertValue(evalResult, unitsByCategory[category][0].value, unitUsed, category) :
        evalResult;

      const resultString = `${finalResult.toFixed(4)} ${unitUsed}`;
      newSteps.push(`Final result: ${resultString}`);
      
      setResult(resultString);
      setSteps(newSteps);
      setError(null);

      // Add to history
      setHistory(prev => [{
        expression: expr,
        result: resultString,
        timestamp: new Date()
      }, ...prev.slice(0, 9)]);

    } catch (err) {
      setError('Invalid expression. Please check your input.');
      setResult(null);
      setSteps([]);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!expression) return;
    evaluateExpression(expression);
  };

  const handleHistoryClick = (item: CalculationHistoryItem) => {
    setExpression(item.expression);
    evaluateExpression(item.expression);
  };

  const handleFormulaClick = (formula: string) => {
    setExpression(formula);
    evaluateExpression(formula);
  };

  return (
    <div className="mt-8 space-y-4">
      <div className="p-6 bg-white/80 backdrop-blur rounded-xl border border-gray-100 shadow-lg">
        <div className="flex items-center justify-between mb-4">
          <div className="space-y-2">
            <Calculator className="w-5 h-5 text-blue-600" />
            <div>
              <h2 className="text-lg font-semibold text-gray-800">{descriptions.title}</h2>
              <p className="text-gray-600 text-sm mt-1">{descriptions.description}</p>
            </div>
            <ul className="text-sm text-gray-500 list-disc ml-5 mt-2">
              {descriptions.features.map((feature, index) => (
                <li key={index}>{feature}</li>
              ))}
            </ul>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowHistory(prev => !prev)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              title="Show History"
            >
              <History className="w-5 h-5 text-gray-600" />
            </button>
            <button
              onClick={() => setShowFormulas(prev => !prev)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              title="Common Formulas"
            >
              <Book className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <input
              type="text"
              value={expression}
              onChange={(e) => setExpression(e.target.value)}
              placeholder="Enter calculation (e.g., 5m + 30cm)"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl bg-white/50 backdrop-blur focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500/20 transition-all outline-none shadow-sm"
            />
            <p className="mt-2 text-sm text-gray-500">
              Supports: +, -, *, / and units from current category
            </p>
          </div>

          {suggestions.length > 0 && (
            <div className="p-3 bg-yellow-50 rounded-lg">
              <p className="text-sm text-yellow-800">
                Did you mean one of these units? {suggestions.join(', ')}
              </p>
            </div>
          )}

          <button
            type="submit"
            className="w-full px-4 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white rounded-xl shadow-lg shadow-blue-500/20 hover:shadow-xl hover:shadow-blue-500/30 transition-all font-medium"
          >
            Calculate
          </button>
        </form>

        {error && (
          <div className="mt-4 p-4 bg-red-50 border border-red-100 rounded-lg">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        {result && (
          <div className="mt-4">
            <div className="p-4 bg-gradient-to-br from-blue-50/50 to-indigo-50/50 rounded-lg border border-blue-100/50">
              <p className="text-lg font-semibold text-gray-800">
                Result: {result}
              </p>
            </div>

            {steps.length > 0 && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-100">
                <div className="flex items-center gap-2 mb-2">
                  <Steps className="w-4 h-4 text-gray-600" />
                  <h3 className="font-medium text-gray-700">Step-by-Step Solution</h3>
                </div>
                <ol className="space-y-2">
                  {steps.map((step, index) => (
                    <li key={index} className="text-sm text-gray-600">
                      {index + 1}. {step}
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        )}
      </div>

      {showHistory && history.length > 0 && (
        <div className="p-6 bg-white/80 backdrop-blur rounded-xl border border-gray-100 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <History className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold text-gray-800">Calculation History</h3>
            </div>
            <button
              onClick={() => setShowHistory(false)}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>
          <div className="space-y-2">
            {history.map((item, index) => (
              <button
                key={index}
                onClick={() => handleHistoryClick(item)}
                className="w-full p-3 text-left bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <p className="text-sm font-medium text-gray-700">{item.expression}</p>
                <p className="text-sm text-gray-500">= {item.result}</p>
                <p className="text-xs text-gray-400 mt-1">
                  {item.timestamp.toLocaleTimeString()}
                </p>
              </button>
            ))}
          </div>
        </div>
      )}

      {showFormulas && commonFormulas[category] && (
        <div className="p-6 bg-white/80 backdrop-blur rounded-xl border border-gray-100 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Book className="w-5 h-5 text-blue-600" />
              <h3 className="font-semibold text-gray-800">
                Common {unitsByCategory[category][0].label} Formulas
              </h3>
            </div>
            <button
              onClick={() => setShowFormulas(false)}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
            >
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>
          <div className="grid gap-3">
            {commonFormulas[category].map((formula, index) => (
              <button
                key={index}
                onClick={() => handleFormulaClick(formula.expression)}
                className="p-4 text-left bg-gray-50 hover:bg-gray-100 rounded-lg transition-all
                  hover:shadow-md hover:scale-[1.02] group"
                onMouseEnter={() => setSelectedFormula(formula)}
                onMouseLeave={() => setSelectedFormula(null)}
                title="Click to use this formula"
                type="button"
              >
                <p className="text-sm font-medium text-gray-700">{formula.name}</p>
                <p className="text-sm text-gray-500">{formula.expression}</p>
                {formula.description && (
                  <p className="text-xs text-gray-400 mt-1 group-hover:text-gray-500">
                    {formula.description}
                  </p>
                )}
              </button>
            ))}
          </div>
          {!commonFormulas[category]?.length && (
            <p className="text-sm text-gray-500 text-center py-4">
              No common formulas available for this category yet.
            </p>
          )}
        </div>
      )}
    </div>
  );
};